import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useClub } from "../contexts/ClubContext";
import { loadClubDoc, saveClubDoc, mkWeek, mkDrill, mkFolder, cp, loadArchivePlan, saveArchivePlan, deleteArchivePlan } from "../lib/data";
import { COLORS, INIT_LEVELS, MINUTE_PRESETS, BF, FF, T, EXTRA_ID, EXTRA_LEVEL, restyleLevel } from "../lib/constants";
import { atLeast, roleLabel } from "../lib/roles";
import Modal from "../components/Modal";
import Home from "./Home";
import Drills from "./Drills";
import DrillDetail from "./DrillDetail";
import Weeks from "./Weeks";
import Lesson from "./Lesson";
import Accounts from "./Accounts";
import WeekView from "./WeekView";
import Planning from "./Planning";
import { currentWeek } from "../lib/planning";
import { ChangePasswordModal, RolesModal } from "../components/AccountMenu";
import { SideNav, MobileBar, BottomBar, BOTTOM_BAR_SPACE, useCollapsed } from "../components/AppNav";
import Library from "./Library";
import NotesBoard from "./NotesBoard";
import NotesPanel from "../components/NotesPanel";
import { loadNotes, saveNote, deleteNote, lessonNoteId, newNoteId } from "../lib/notes";
import { loadFavs, saveFavs, libFavKey, deckFavKey } from "../lib/favs";
import { fmtDur, parseDur } from "../lib/blocks";
import { shortLabel } from "../components/ui";
import LibItem from "./LibItem";
import { planLessons, loadLibrary, saveLibItem, deleteLibItem, newLibId, mkLibLesson, mkLibDrill, copyAsLesson, copyAsDrill,
  loadOfficial, copyPlanToOfficial, saveOfficial, deleteOfficial, planColorMap } from "../lib/library";
import PublishBanner from "../components/PublishBanner";

// Vult voor elke seizoensplan-level ontbrekende weken aan met lege standaardweken, en
// zorgt dat elke week een tags-array heeft. Wordt zowel op de live als de concept-versie
// van de data toegepast, zodat beide altijd dezelfde structuur hebben.
function normalizeData(levels, rawData) {
  var d = rawData || {};
  levels.forEach(function (v) {
    if (v.type === "drills") return;
    // Gearchiveerde plannen staan (sinds build 2.5) in een eigen document; niet aanvullen.
    if (v.archived && !d[v.id]) return;
    if (!d[v.id]) { d[v.id] = {}; for (var i = 1; i <= 20; i++) d[v.id][i] = mkWeek(i); }
    Object.keys(d[v.id]).forEach(function (wk) { if (!d[v.id][wk].tags) d[v.id][wk].tags = []; });
  });
  return d;
}

export default function ClubApp() {
  // Seizoensplannen zijn gedeeld: ze staan bij planClubId (Atalanta) en elke club leest
  // die. Training Deck (drills) en extra oefeningen zijn van de club zelf (clubId).
  var { clubId, hasDeck, planClubId, club, solo } = useClub();
  var { profile, logout } = useAuth();
  var navigate = useNavigate();
  // Rollen: trainer = alleen lezen, hoofdtrainer = + extra oefeningen en accounts van
  // trainers, editor = + seizoensplannen en Training Deck bewerken, creator = alles.
  var canEditRole = atLeast(profile, "editor");
  var [viewOnly, setViewOnly] = useState(false);
  // Zijbalk (laptop) in- of uitgeklapt, en het open account-venster (wachtwoord/rollen).
  var [collapsed, toggleCollapsed] = useCollapsed();
  var [acctModal, setAcctModal] = useState(null);
  var canEdit = canEditRole && !viewOnly;
  var canEditExtra = atLeast(profile, "hoofdtrainer") && !viewOnly;

  var [mob, setMob] = useState(window.innerWidth < 640);
  var [wide, setWide] = useState(window.innerWidth >= 900);
  // Weekoverzicht: gekozen week, groep (index in de actieve plannen) en les (A/B).
  var [wvWeek, setWvWeek] = useState(1);
  var [wvSel, setWvSel] = useState(0);
  // Les A/B per groep in het weekoverzicht: { planId: 0 | 1 } (standaard Les A).
  var [wvTab, setWvTab] = useState({});
  var [lastOv, setLastOv] = useState("home");
  // Seizoensplanning van deze club (startdatum + vakantieweken), zie lib/planning.js.
  var [planning, setPlanning] = useState(null);
  var planTimer = useRef(null);
  var [levels, setLevels] = useState(null);
  // Seizoensplan-inhoud (weken/lessen) bestaat als twee versies: publishedData is wat
  // niet-bewerkende trainers en Bekijkmodus zien, draftData is de werkkopie die een
  // bewerkende trainer ziet/wijzigt. Pas na een expliciete "Publiceren"-klik wordt de
  // concept-versie live. `data` hieronder is simpelweg "welke van de twee er nu geldt",
  // zodat alle onderliggende schermen ongewijzigd met één `data`-prop kunnen blijven werken.
  var [publishedData, setPublishedData] = useState(null);
  var [draftData, setDraftData] = useState(null);
  var [publishing, setPublishing] = useState(false);
  var [justPublished, setJustPublished] = useState(false);
  // Weken van gearchiveerde plannen, pas geladen als een editor zo'n plan opent.
  var [archiveData, setArchiveData] = useState({});
  var archiveRef = useRef({});
  var archTimers = useRef({});
  var baseData = canEdit ? draftData : publishedData;
  var data = baseData ? Object.assign({}, baseData, archiveData) : baseData;
  var hasUnpublished = !!(canEdit && draftData && publishedData && JSON.stringify(draftData) !== JSON.stringify(publishedData));
  var draftTimer = useRef(null);

  var [drills, setDrills] = useState(null);
  var [loading, setLoading] = useState(true);
  var [scr, setScr] = useState("home");
  var [sel, setSel] = useState(null);
  var [selW, setSelW] = useState(null);
  var [selDi, setSelDi] = useState(null);
  var [curFolder, setCurFolder] = useState(null);
  var [tab, setTab] = useState(0);
  var [mdl, setMdl] = useState(null);
  var [sav, setSav] = useState(false);
  var [savLabel, setSavLabel] = useState("OPGESLAGEN ✓");
  var [nn, setNn] = useState("");
  var [nc, setNc] = useState(0);
  var [delT, setDelT] = useState(null);
  var [editLvl, setEditLvl] = useState(null);
  var [editName, setEditName] = useState("");
  var [editColor, setEditColor] = useState(0);
  var [moveLvlId, setMoveLvlId] = useState(null);
  var [weekFilter, setWeekFilter] = useState([]);
  var [drillFilter, setDrillFilter] = useState([]);
  var [moveItem, setMoveItem] = useState(null);
  var [copyWeekSrc, setCopyWeekSrc] = useState(null);
  var [moveWeekSrc, setMoveWeekSrc] = useState(null);
  var [dupSourceId, setDupSourceId] = useState(null);
  var [dupName, setDupName] = useState("");
  var [dupColor, setDupColor] = useState(0);
  var [timeEdit, setTimeEdit] = useState(null);
  var [customTime, setCustomTime] = useState("");

  // ---- Bibliotheek (build 2.5) ----
  var [libKind, setLibKind] = useState("lessen");
  var [lesSrc, setLesSrc] = useState("plan");
  var [oefSrc, setOefSrc] = useState(hasDeck ? "deck" : (solo ? "mine" : "club"));
  var [libColor, setLibColor] = useState(null);
  var [libTags, setLibTags] = useState([]);
  var [libItems, setLibItems] = useState(null);
  var libItemsRef = useRef(null);
  var libTimers = useRef({});
  var [libError, setLibError] = useState("");
  var [libSel, setLibSel] = useState(null);
  // Favorieten (build 2.6): lijst sleutels per trainer; de chip geldt voor alle tabbladen.
  var [favs, setFavs] = useState(null);
  var [favOnly, setFavOnly] = useState(false);
  var [migrating, setMigrating] = useState(false);
  var [copyPlanId, setCopyPlanId] = useState(null);
  var [copyingPlan, setCopyingPlan] = useState(false);
  var owner = profile ? { uid: profile.uid, name: profile.name || profile.email || "" } : null;

  // ---- Notities (build 2.5) ----
  var [notes, setNotes] = useState(null);
  var notesRef = useRef(null);
  var noteTimers = useRef({});
  var [noteStatus, setNoteStatus] = useState("");
  var [notesError, setNotesError] = useState("");
  var [selNoteId, setSelNoteId] = useState(null);

  useEffect(function () {
    var h = function () { setMob(window.innerWidth < 640); setWide(window.innerWidth >= 900); };
    window.addEventListener("resize", h);
    return function () { window.removeEventListener("resize", h); };
  }, []);

  useEffect(function () {
    return function () {
      if (draftTimer.current) clearTimeout(draftTimer.current);
      if (planTimer.current) clearTimeout(planTimer.current);
    };
  }, []);

  var load = useCallback(async function () {
    setLoading(true);
    // Alle documenten tegelijk ophalen i.p.v. na elkaar: scheelt een hele rij
    // netwerk-rondes, vooral merkbaar op mobiel internet op de baan.
    var results = await Promise.all([
      loadClubDoc(planClubId, "levels"),
      loadClubDoc(planClubId, "data"),
      // De concept-versie is alleen voor editors (en ook alleen voor hen leesbaar).
      canEditRole ? loadClubDoc(planClubId, "data_draft") : Promise.resolve(null),
      hasDeck ? loadClubDoc(clubId, "drills") : Promise.resolve(null),
      loadClubDoc(clubId, "extraDrills"),
      loadClubDoc(clubId, "planning"),
    ]);
    var l = results[0], rawLive = results[1], rawDraft = results[2];
    var r = results[3], extra = results[4], pl = results[5];
    if (!l) l = cp(INIT_LEVELS);
    l = l.map(restyleLevel);
    var liveNorm = normalizeData(l, rawLive);
    var draftNorm = rawDraft ? normalizeData(l, rawDraft) : cp(liveNorm);
    if (!r) r = {};
    r[EXTRA_ID] = Array.isArray(extra) ? extra : [];
    l.forEach(function (v) {
      if (v.type === "drills" && !hasDeck) return;
      if (v.type === "drills" && !r[v.id]) r[v.id] = [];
      if (v.type === "drills" && r[v.id]) {
        r[v.id] = r[v.id].map(function (item) {
          if (!item.type) return Object.assign({}, item, { type: "drill", folderId: null });
          return item;
        });
      }
    });
    // Weekoverzicht meteen op de week van vandaag zetten.
    var maxW = maxWeekOf(l, liveNorm);
    setWvWeek(currentWeek(pl, maxW).week);
    setPlanning(pl);
    setLevels(l); setPublishedData(liveNorm); setDraftData(draftNorm); setDrills(r); setLoading(false);
  }, [clubId, planClubId, hasDeck, canEditRole]);
  useEffect(function () { load(); }, [load]);

  // Favorieten pas ophalen als de bibliotheek (of een oefening) open gaat.
  var favWanted = scr === "bib" || scr === "libItem" || scr === "drillD";
  useEffect(function () {
    if (!favWanted || favs !== null || !profile) return;
    var cancelled = false;
    loadFavs(clubId, profile.uid).then(function (ids) { if (!cancelled) setFavs(ids); })
      .catch(function (e) { console.error("Favs load error:", e); if (!cancelled) setFavs([]); });
    return function () { cancelled = true; };
  }, [favWanted, favs, clubId, profile]);
  function isFav(k) { return !!favs && favs.indexOf(k) >= 0; }
  function toggleFav(k) {
    if (!profile) return;
    var cur = favs || [];
    var next = cur.indexOf(k) >= 0 ? cur.filter(function (x) { return x !== k; }) : cur.concat([k]);
    setFavs(next);
    saveFavs(clubId, profile.uid, next).catch(function (e) { console.error("Favs save error:", e); });
  }

  // Eigen en gedeelde lessen/oefeningen pas ophalen als de bibliotheek open gaat.
  var libWanted = scr === "bib" || scr === "libItem" || mdl === "copyPlanLib";
  useEffect(function () {
    if (!libWanted || libItems !== null || !profile) return;
    var cancelled = false;
    setLibError("");
    // Eigen + gedeelde items van de club, en de losse lessen uit het seizoensplan.
    Promise.all([loadLibrary(clubId, profile.uid), loadOfficial(planClubId)]).then(function (res) {
      if (cancelled) return;
      var list = res[1].concat(res[0]);
      libItemsRef.current = list; setLibItems(list);
    }).catch(function (e) {
      console.error("Library load error:", e);
      if (cancelled) return;
      setLibError("Kon de lessen en oefeningen van de club niet laden. Probeer het later opnieuw.");
      libItemsRef.current = []; setLibItems([]);
    });
    return function () { cancelled = true; };
  }, [libWanted, libItems, clubId, planClubId, profile]);

  // Training Deck in de bibliotheek: zorg dat `sel` op een deck-sectie staat, zodat
  // toevoegen/verplaatsen in die sectie gebeurt.
  useEffect(function () {
    if (scr !== "bib" || libKind !== "oefeningen" || oefSrc !== "deck" || !levels || !hasDeck) return;
    var secs = levels.filter(function (l) { return l.type === "drills"; });
    if (secs.length && !secs.some(function (d) { return d.id === sel; })) { setSel(secs[0].id); setCurFolder(null); setDrillFilter([]); }
  }, [scr, libKind, oefSrc, levels, hasDeck, sel]);

  // Notities pas ophalen als je een les, het weekoverzicht of het notitieblok opent.
  var notesWanted = scr === "lesson" || scr === "week" || (scr === "bib" && libKind === "notities");
  useEffect(function () {
    if (!notesWanted || notes !== null || !profile) return;
    var cancelled = false;
    loadNotes(clubId, profile.uid).then(function (list) {
      if (cancelled) return;
      notesRef.current = list; setNotes(list);
    }).catch(function (e) {
      console.error("Notes load error:", e);
      if (cancelled) return;
      setNotesError("Kon de notities niet laden.");
      notesRef.current = []; setNotes([]);
    });
    return function () { cancelled = true; };
  }, [notesWanted, notes, clubId, profile]);

  // Onthoud waar je in het gewone Overzicht was, zodat de schakelaar je daar terugzet.
  useEffect(function () {
    if (scr === "home" || scr === "weeks" || scr === "lesson") setLastOv(scr);
    window.scrollTo(0, 0);
  }, [scr]);

  // Concept-wijzigingen (weken/lessen-inhoud): direct lokaal zichtbaar, maar pas na een
  // korte pauze in typen weggeschreven naar de aparte concept-opslag (niet naar de live
  // data), en pas echt live voor anderen na een expliciete publicatie. Geen apart
  // "concept opgeslagen"-blokje meer hierbij — de oranje publicatiebanner is zolang er
  // niet-gepubliceerde wijzigingen zijn al zichtbaar, dat is genoeg indicatie.
  function persistDraftData(nd) {
    setDraftData(nd);
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(function () {
      draftTimer.current = null;
      saveClubDoc(planClubId, "data_draft", nd);
    }, 900);
  }

  function persistPlanning(np) {
    setPlanning(np);
    if (planTimer.current) clearTimeout(planTimer.current);
    planTimer.current = setTimeout(async function () {
      planTimer.current = null;
      setSavLabel("OPGESLAGEN ✓");
      setSav(true);
      await saveClubDoc(clubId, "planning", np);
      setTimeout(function () { setSav(false); }, 600);
    }, 700);
  }

  async function publish() {
    if (draftTimer.current) { clearTimeout(draftTimer.current); draftTimer.current = null; }
    setPublishing(true);
    await saveClubDoc(planClubId, "data_draft", draftData);
    await saveClubDoc(planClubId, "data", draftData);
    setPublishedData(cp(draftData));
    setPublishing(false);
    setJustPublished(true);
    setTimeout(function () { setJustPublished(false); }, 1400);
  }

  // Niveaus/oefeningen (levels + drills) blijven, zoals voorheen, meteen live opslaan —
  // dat zijn bewuste, losse acties (een niveau toevoegen/hernoemen/verwijderen), geen
  // doorlopend typewerk zoals de weekinhoud.
  async function persistLevelsAndDrills(nl, nr) {
    if (nl) setLevels(nl);
    if (nr) setDrills(nr);
    setSavLabel("OPGESLAGEN ✓");
    setSav(true);
    if (nl) await saveClubDoc(planClubId, "levels", nl);
    if (nr) {
      // Extra oefeningen en de Training Deck staan in aparte documenten (met aparte
      // schrijfrechten); alleen het deel dat echt veranderd is wordt weggeschreven.
      var prev = drills || {};
      var extraChanged = JSON.stringify(nr[EXTRA_ID] || []) !== JSON.stringify(prev[EXTRA_ID] || []);
      var deckNew = Object.assign({}, nr); delete deckNew[EXTRA_ID];
      var deckPrev = Object.assign({}, prev); delete deckPrev[EXTRA_ID];
      if (extraChanged) await saveClubDoc(clubId, "extraDrills", nr[EXTRA_ID] || []);
      if (JSON.stringify(deckNew) !== JSON.stringify(deckPrev)) await saveClubDoc(clubId, "drills", deckNew);
    }
    setTimeout(function () { setSav(false); }, 600);
  }

  // Een nieuw of verwijderd seizoensplan raakt zowel levels als de (lege) weekdata voor
  // dat plan — dat schrijven we meteen naar zowel live als concept, zodat beide versies
  // consistent blijven (er valt bij aanmaken/verwijderen niets "halfs" te publiceren).
  async function persistLevelAndData(nl, ndLive, ndDraft) {
    setLevels(nl); setPublishedData(ndLive); setDraftData(ndDraft);
    setSavLabel("OPGESLAGEN ✓");
    setSav(true);
    await Promise.all([
      saveClubDoc(planClubId, "levels", nl),
      saveClubDoc(planClubId, "data", ndLive),
      saveClubDoc(planClubId, "data_draft", ndDraft),
    ]);
    setTimeout(function () { setSav(false); }, 600);
  }

  // ---- Archief: weken van gearchiveerde plannen in eigen documenten ----
  function setArch(next) { archiveRef.current = next; setArchiveData(next); }
  // Laadt de weken van een gearchiveerd plan (eenmalig). Staat het plan nog in het oude
  // plannen-document (van vóór de verhuizing), dan blijft het daar.
  async function ensureArchive(id) {
    if (archiveRef.current[id]) return archiveRef.current[id];
    var l = levels && levels.find(function (x) { return x.id === id; });
    if (!l || !l.archived || !canEditRole) return null;
    try {
      var weeks = await loadArchivePlan(planClubId, id);
      if (weeks) { var n = Object.assign({}, archiveRef.current); n[id] = weeks; setArch(n); return weeks; }
    } catch (e) { console.error("Archive load error:", e); }
    return null;
  }
  function saveArchiveSoon(id, weeks) {
    if (archTimers.current[id]) clearTimeout(archTimers.current[id]);
    archTimers.current[id] = setTimeout(function () {
      delete archTimers.current[id];
      saveArchivePlan(planClubId, id, weeks).then(flashSaved).catch(function (e) { console.error("Archive save error:", e); alert("Opslaan in het archief is niet gelukt."); });
    }, 900);
  }
  // Kopie van alle weken die je nu ziet (concept + geladen archief).
  function cpPlanData() { return cp(Object.assign({}, draftData, archiveRef.current)); }
  // Wijzigingen terugzetten: archiefplannen naar hun eigen document, de rest naar het concept.
  function persistPlanData(n) {
    var arch = Object.assign({}, archiveRef.current), draft = {}, archChanged = false, draftChanged = false;
    Object.keys(n).forEach(function (k) {
      if (archiveRef.current[k]) {
        if (JSON.stringify(n[k]) !== JSON.stringify(archiveRef.current[k])) { arch[k] = n[k]; archChanged = true; saveArchiveSoon(k, n[k]); }
      } else draft[k] = n[k];
    });
    if (archChanged) setArch(arch);
    Object.keys(draftData || {}).forEach(function (k) { if (!(k in draft) && !archiveRef.current[k]) draftChanged = true; });
    if (draftChanged || JSON.stringify(draft) !== JSON.stringify(draftData)) persistDraftData(draft);
  }

  function setField(f, v) { var n = cpPlanData(); n[sel][selW][f] = v; persistPlanData(n); }
  function setEx(lk, ek, f, v) { var n = cpPlanData(); var les = n[sel][selW][lk]; if (!les[ek]) les[ek] = { name: "", description: "", accents: [], time: 0 }; les[ek][f] = v; persistPlanData(n); }
  // Lesopbouw (build 2.6): de hele les in één keer vervangen (volgorde + blokken).
  function setLesson(lk, v) { var n = cpPlanData(); n[sel][selW][lk] = v; persistPlanData(n); }
  function setExtras(v) { var n = cpPlanData(); n[sel][selW].extras = v; persistPlanData(n); }
  function setWeekTags(v) { var n = cpPlanData(); n[sel][selW].tags = v; persistPlanData(n); }
  function addWeek() { var n = cpPlanData(); if (!n[sel]) n[sel] = {}; var ks = Object.keys(n[sel]).map(Number); var mx = ks.length ? Math.max.apply(null, ks) : 0; n[sel][mx + 1] = mkWeek(mx + 1); persistPlanData(n); }
  // Week verplaatsen naar een andere positie; alle weken worden daarna opnieuw
  // genummerd (1, 2, 3, ...), net als bij verwijderen.
  function moveWeek(from, to) {
    var n = cpPlanData();
    var keys = Object.keys(n[sel]).map(Number).sort(function (a, b) { return a - b; });
    var arr = keys.map(function (k) { return n[sel][k]; });
    var fi = keys.indexOf(from), ti = to - 1;
    if (fi < 0 || ti < 0 || ti >= arr.length || fi === ti) { setMdl(null); setMoveWeekSrc(null); return; }
    var item = arr.splice(fi, 1)[0];
    arr.splice(ti, 0, item);
    n[sel] = {};
    arr.forEach(function (d2, i) { d2.week = i + 1; n[sel][i + 1] = d2; });
    persistPlanData(n);
    setMdl(null); setMoveWeekSrc(null);
  }
  function openMoveWeek(w) { setMoveWeekSrc(w); setMdl("moveWeek"); }
  function removeWeek(w) { var n = cpPlanData(); delete n[sel][w]; var arr = Object.values(n[sel]).sort(function (a, b) { return a.week - b.week; }); n[sel] = {}; arr.forEach(function (d2, i) { d2.week = i + 1; n[sel][i + 1] = d2; }); persistPlanData(n); setMdl(null); if (selW === w) setScr("weeks"); }

  // Week dupliceren: kopie komt direct na het origineel, alles daarna schuift een nummer door.
  function duplicateWeek(w) {
    var n = cpPlanData();
    var keys = Object.keys(n[sel]).map(Number).sort(function (a, b) { return a - b; });
    var ordered = keys.map(function (k) { return n[sel][k]; });
    var idx = keys.indexOf(w);
    if (idx < 0) return;
    var dup = cp(ordered[idx]);
    ordered.splice(idx + 1, 0, dup);
    var rebuilt = {};
    ordered.forEach(function (wk, i2) { wk.week = i2 + 1; rebuilt[i2 + 1] = wk; });
    n[sel] = rebuilt;
    persistPlanData(n);
  }

  // Week kopiëren naar een ander seizoensplan: komt aan het eind van dat plan te staan.
  function copyWeekToPlan(w, targetId) {
    var n = cpPlanData();
    if (!n[targetId]) n[targetId] = {};
    var existingNums = Object.keys(n[targetId]).map(Number);
    var nextNum = existingNums.length > 0 ? Math.max.apply(null, existingNums) + 1 : 1;
    var dup = cp(n[sel][w]);
    dup.week = nextNum;
    n[targetId][nextNum] = dup;
    persistPlanData(n);
    setMdl(null); setCopyWeekSrc(null);
  }

  function addDrillItem() { var n = cp(drills); var drill = mkDrill(); drill.folderId = curFolder; n[sel].push(drill); persistLevelsAndDrills(null, n); }
  function addFolder() { var n = cp(drills); n[sel].push(mkFolder(curFolder)); persistLevelsAndDrills(null, n); }
  function setDrillField(id, f, v) {
    var n = cp(drills);
    var idx = n[sel].findIndex(function (x) { return x.id === id; });
    if (idx >= 0) { n[sel][idx][f] = v; persistLevelsAndDrills(null, n); }
  }
  function removeDrillItem(id) {
    var n = cp(drills);
    var item = n[sel].find(function (x) { return x.id === id; });
    if (item && item.type === "folder") {
      n[sel].forEach(function (x) { if (x.folderId === id) x.folderId = item.parentId; });
    }
    n[sel] = n[sel].filter(function (x) { return x.id !== id; });
    persistLevelsAndDrills(null, n);
    setMdl(null); setSelDi(null); setScr("drills");
  }
  function renameFolder(id, newName) { setDrillField(id, "name", newName); }

  function moveDrillTo(targetFolderId) {
    if (!moveItem) return;
    var item = drills[sel].find(function (x) { return x.id === moveItem; });
    if (item && item.type === "folder") {
      var isDescendant = function (folderId) {
        if (!folderId) return false;
        if (folderId === moveItem) return true;
        var f = drills[sel].find(function (x) { return x.id === folderId; });
        if (!f) return false;
        return isDescendant(f.parentId);
      };
      if (isDescendant(targetFolderId)) {
        alert("Kan een map niet naar zichzelf of een eigen submap verplaatsen.");
        setMoveItem(null); setMdl(null);
        return;
      }
    }
    var n = cp(drills);
    var idx = n[sel].findIndex(function (x) { return x.id === moveItem; });
    if (idx >= 0) {
      if (n[sel][idx].type === "folder") n[sel][idx].parentId = targetFolderId;
      else n[sel][idx].folderId = targetFolderId;
      persistLevelsAndDrills(null, n);
    }
    setMoveItem(null); setMdl(null);
  }

  function addLevel(type) {
    if (!nn.trim()) return;
    var id = nn.trim().toLowerCase().replace(/[^a-z0-9]/g, "_") + "_" + Date.now().toString(36);
    var c = COLORS[nc % COLORS.length];
    // Nieuwe seizoensplannen beginnen als concept: pas zichtbaar voor trainers als een
    // editor ze activeert.
    var nlv = { id: id, label: nn.trim(), color: c.color, bg: c.bg, border: c.border, type: type };
    if (type === "plan") nlv.concept = true;
    var nl = levels.concat([nlv]);
    if (type === "plan") {
      // Nieuw seizoensplan begint leeg; weken voeg je zelf toe met "Week toevoegen".
      var ndLive = cp(publishedData); ndLive[id] = {};
      var ndDraft = cp(draftData); ndDraft[id] = cp(ndLive[id]);
      persistLevelAndData(nl, ndLive, ndDraft);
    } else {
      var nr = cp(drills); nr[id] = [];
      persistLevelsAndDrills(nl, nr);
      setSel(id); setCurFolder(null); setDrillFilter([]);
    }
    setMdl(null); setNn("");
  }
  function removeLevel(id) {
    var nl = levels.filter(function (l) { return l.id !== id; });
    var lv2 = levels.find(function (l) { return l.id === id; });
    if (lv2 && lv2.type === "drills") { var nr = cp(drills); delete nr[id]; persistLevelsAndDrills(nl, nr); }
    else {
      var ndLive = cp(publishedData); delete ndLive[id];
      var ndDraft = cp(draftData); delete ndDraft[id];
      persistLevelAndData(nl, ndLive, ndDraft);
      if (lv2 && lv2.archived) {
        deleteArchivePlan(planClubId, id).catch(function (e) { console.error("Archive delete error:", e); });
        var na = Object.assign({}, archiveRef.current); delete na[id]; setArch(na);
      }
    }
    setMdl(null);
  }
  function renameLevel() {
    if (!editName.trim() || !editLvl) return;
    var nl = levels.map(function (l) { return l.id === editLvl ? Object.assign({}, l, COLORS[editColor] || {}, { label: editName.trim() }) : l; });
    persistLevelsAndDrills(nl, null);
    setMdl(null); setEditLvl(null); setEditName("");
  }
  // Archiveren verhuist de weken naar een eigen archiefdocument (en uit het plannen-
  // document); terugzetten doet het omgekeerde. Eerst het archiefdocument schrijven,
  // daarna pas de plannen, zodat er nooit iets verloren gaat.
  async function toggleArchive(id) {
    var l0 = levels.find(function (l) { return l.id === id; });
    if (!l0) return;
    var nl = levels.map(function (l) { return l.id === id ? Object.assign({}, l, { archived: !l.archived, concept: false }) : l; });
    try {
      if (!l0.archived) {
        var weeks = (draftData && draftData[id]) || (publishedData && publishedData[id]) || {};
        await saveArchivePlan(planClubId, id, weeks);
        var na = Object.assign({}, archiveRef.current); na[id] = cp(weeks); setArch(na);
        var ndLive = cp(publishedData); delete ndLive[id];
        var ndDraft = cp(draftData); delete ndDraft[id];
        await persistLevelAndData(nl, ndLive, ndDraft);
      } else {
        var aw = await ensureArchive(id);
        if (aw) {
          var ndLive2 = cp(publishedData); ndLive2[id] = cp(aw);
          var ndDraft2 = cp(draftData); ndDraft2[id] = cp(aw);
          await persistLevelAndData(nl, ndLive2, ndDraft2);
          await deleteArchivePlan(planClubId, id);
          var nb = Object.assign({}, archiveRef.current); delete nb[id]; setArch(nb);
        } else {
          persistLevelsAndDrills(nl, null); // oud archiefplan dat nog in het plannen-document staat
        }
      }
    } catch (e) {
      console.error("Archive toggle error:", e);
      alert("Dat is niet gelukt. Controleer je internet en probeer het opnieuw.");
    }
  }
  // Concept <-> actief: een concept-plan is alleen zichtbaar voor editor en creator.
  function toggleConcept(id) {
    var nl = levels.map(function (l) { return l.id === id ? Object.assign({}, l, { concept: !l.concept, archived: false }) : l; });
    persistLevelsAndDrills(nl, null);
  }

  // Seizoensplan dupliceren: kopieert een bestaand (gepubliceerd of concept) plan meteen
  // volledig naar een nieuw plan — dit is bewust géén concept-actie, want je kopieert al
  // bestaande, al eerder afgeronde inhoud, geen nieuw typewerk.
  function duplicatePlan() {
    if (!dupName.trim() || !dupSourceId) return;
    var id = dupName.trim().toLowerCase().replace(/[^a-z0-9]/g, "_") + "_" + Date.now().toString(36);
    var c = COLORS[dupColor % COLORS.length];
    var nl = levels.concat([{ id: id, label: dupName.trim(), color: c.color, bg: c.bg, border: c.border, type: "plan", concept: true }]);
    var source = (archiveRef.current[dupSourceId]) || (draftData && draftData[dupSourceId]) || (publishedData && publishedData[dupSourceId]) || {};
    var ndLive = cp(publishedData); ndLive[id] = cp(source);
    var ndDraft = cp(draftData); ndDraft[id] = cp(source);
    persistLevelAndData(nl, ndLive, ndDraft);
    setMdl(null); setDupSourceId(null); setDupName(""); setDupColor(0);
  }

  // ---- Bibliotheek: eigen items bewerken, kopiëren, verwijderen ----
  function flashSaved() { setSavLabel("OPGESLAGEN ✓"); setSav(true); setTimeout(function () { setSav(false); }, 600); }
  function libFail(e) { console.error("Library save error:", e); setLibError("Opslaan is niet gelukt. Controleer je internet en probeer het opnieuw."); }
  function setLibList(next) { libItemsRef.current = next; setLibItems(next); }
  function changeLib(id, mutate) {
    var next = (libItemsRef.current || []).map(function (x) { if (x.id !== id) return x; var c = cp(x); mutate(c); return c; });
    setLibList(next);
    var item = next.find(function (x) { return x.id === id; });
    if (libTimers.current[id]) clearTimeout(libTimers.current[id]);
    libTimers.current[id] = setTimeout(function () {
      delete libTimers.current[id];
      (item.src === "plan" ? saveOfficial(planClubId, item) : saveLibItem(clubId, item)).then(flashSaved).catch(libFail);
    }, 700);
  }
  async function addLibItem(item, open) {
    item.id = newLibId(clubId);
    if (solo) item.shared = false;
    try {
      await saveLibItem(clubId, item, true);
    } catch (e) { libFail(e); return; }
    setLibList((libItemsRef.current || []).concat([item]));
    if (item.kind === "lesson") { setLibKind("lessen"); setLesSrc("mine"); } else { setLibKind("oefeningen"); setOefSrc("mine"); }
    if (open) { setLibSel({ type: "lib", id: item.id }); setScr("libItem"); }
  }
  function newLib(kind) {
    if (!owner) return;
    addLibItem(kind === "lesson" ? mkLibLesson(owner, libColor || "rood") : mkLibDrill(owner), true);
  }
  function copyLib(src) {
    if (!owner) return;
    var n = src.kind === "lesson" ? copyAsLesson(owner, src) : copyAsDrill(owner, src);
    if (src.ownerUid === owner.uid) { n.name = (src.name || "") + " (kopie)"; n.copiedFrom = src.copiedFrom || ""; }
    addLibItem(n, true);
  }
  async function removeLib(id) {
    var it = (libItemsRef.current || []).find(function (x) { return x.id === id; });
    try { await (it && it.src === "plan" ? deleteOfficial(planClubId, id) : deleteLibItem(clubId, id)); }
    catch (e) { libFail(e); return; }
    if (libTimers.current[id]) { clearTimeout(libTimers.current[id]); delete libTimers.current[id]; }
    setLibList((libItemsRef.current || []).filter(function (x) { return x.id !== id; }));
    setLibSel(null); setScr("bib");
  }
  function isEmptyLib(it) {
    if (!it || it.name) return false;
    if (it.kind === "drill") return !it.description && !it.extraInfo && !it.photo && !(it.tags || []).length && !it.diagram;
    var p = it.parts || {};
    return !Object.keys(p).some(function (k) { return p[k] && ((p[k].description || "").trim() || (p[k].accents || []).length || p[k].diagram); });
  }
  // Terug naar de lijst; een nieuwe les/oefening waarin niets is ingevuld ruimen we op.
  function backFromLib(item) {
    if (item && item.src !== "plan" && owner && item.ownerUid === owner.uid && isEmptyLib(item)) removeLib(item.id);
    else { setLibSel(null); setScr("bib"); }
  }
  // De oude lijst "extra oefeningen" (clubs/{id}/config/extraDrills) overzetten naar
  // gedeelde oefeningen in de bibliotheek. Mapnamen worden tags.
  async function migrateLegacy() {
    var list = (drills && drills[EXTRA_ID]) || [];
    if (!owner || !list.length) return;
    setMigrating(true); setLibError("");
    function folderNames(fid) {
      var names = [];
      var f = list.find(function (x) { return x.id === fid; });
      while (f) { if (f.name) names.unshift(f.name); f = f.parentId ? list.find(function (x) { return x.id === f.parentId; }) : null; }
      return names;
    }
    try {
      var added = [];
      for (var i = 0; i < list.length; i++) {
        var d = list[i];
        if (d.type !== "drill") continue;
        var n = mkLibDrill(owner);
        n.shared = !solo;
        ["name", "description", "extraInfo", "photo", "diagram"].forEach(function (k) { if (d[k] !== undefined && d[k] !== null) n[k] = cp(d[k]); });
        var tags = (d.tags || []).slice();
        folderNames(d.folderId).forEach(function (t) { if (tags.indexOf(t) < 0) tags.push(t); });
        n.tags = tags;
        n.legacyId = String(d.id);
        n.id = newLibId(clubId);
        await saveLibItem(clubId, n, true);
        added.push(n);
      }
      await saveClubDoc(clubId, "extraDrills", []);
      var nr = cp(drills); nr[EXTRA_ID] = [];
      setDrills(nr);
      setLibList((libItemsRef.current || []).concat(added));
      flashSaved();
    } catch (e) {
      libFail(e);
    }
    setMigrating(false);
  }

  // Een heel seizoensplan kopiëren naar de losse lessen (editor/creator). Neemt de
  // versie die je nu ziet (voor een editor: de conceptversie).
  async function copyPlanToLibrary() {
    var p = levels.find(function (l) { return l.id === copyPlanId; });
    if (!p) { setMdl(null); return; }
    var src = planLessons([p], data);
    setCopyingPlan(true); setLibError("");
    try {
      var added = await copyPlanToOfficial(planClubId, src);
      if (libItemsRef.current) {
        var ids = added.map(function (x) { return x.id; });
        setLibList(added.concat(libItemsRef.current.filter(function (x) { return ids.indexOf(x.id) < 0; })));
      }
      setMdl(null); setCopyPlanId(null);
      setSavLabel(added.length + (added.length === 1 ? " LES" : " LESSEN") + " GEKOPIEERD ✓"); setSav(true);
      setTimeout(function () { setSav(false); setSavLabel("OPGESLAGEN ✓"); }, 1800);
    } catch (e) {
      console.error("Copy plan error:", e);
      alert("Kopiëren is niet gelukt. Controleer je internet en probeer het opnieuw.");
    }
    setCopyingPlan(false);
  }

  // ---- Notities ----
  function setNotesList(next) { notesRef.current = next; setNotes(next); }
  function noteIsEmpty(n) { return !(n.text || "").trim() && !(n.title || "").trim(); }
  function scheduleNoteSave(id) {
    if (noteTimers.current[id]) clearTimeout(noteTimers.current[id]);
    setNoteStatus("saving");
    noteTimers.current[id] = setTimeout(async function () {
      delete noteTimers.current[id];
      var n = (notesRef.current || []).find(function (x) { return x.id === id; });
      if (!n) return;
      try {
        if (noteIsEmpty(n) && n.kind === "lesson") {
          // Lege notitie bij een les: niet bewaren (en opruimen als hij al bestond).
          if (n.saved) await deleteNote(clubId, id);
          setNotesList((notesRef.current || []).map(function (x) { return x.id === id ? Object.assign({}, x, { saved: false }) : x; }));
        } else if (!noteIsEmpty(n)) {
          await saveNote(clubId, n);
          setNotesList((notesRef.current || []).map(function (x) { return x.id === id ? Object.assign({}, x, { saved: true }) : x; }));
        }
        setNoteStatus("saved");
      } catch (e) { console.error("Note save error:", e); setNoteStatus("error"); }
    }, 800);
  }
  function upsertNote(n) {
    var list = notesRef.current || [];
    var exists = list.some(function (x) { return x.id === n.id; });
    setNotesList(exists ? list.map(function (x) { return x.id === n.id ? n : x; }) : [n].concat(list));
    scheduleNoteSave(n.id);
  }
  function lessonLabelOf(level, week, les) { return shortLabel(level.label) + " · week " + week + " · Les " + (les ? "B" : "A"); }
  function editLessonNote(level, week, les, patch) {
    if (!owner) return;
    var id = lessonNoteId(owner.uid, level.id, week, les);
    var cur = (notesRef.current || []).find(function (x) { return x.id === id; });
    var base = cur || { id: id, kind: "lesson", ownerUid: owner.uid, ownerName: owner.name, shared: false, text: "", planId: level.id, week: Number(week), les: les ? 1 : 0, saved: false };
    upsertNote(Object.assign({}, base, { lessonLabel: lessonLabelOf(level, week, les), color: level.color }, patch, { updatedMs: Date.now() }));
  }
  function changeNote(n, patch) { upsertNote(Object.assign({}, n, patch, { updatedMs: Date.now() })); }
  function newFreeNote() {
    if (!owner) return;
    var n = { id: newNoteId(clubId), kind: "free", ownerUid: owner.uid, ownerName: owner.name, shared: false, title: "", text: "", saved: false, updatedMs: Date.now() };
    setNotesList([n].concat(notesRef.current || []));
    setSelNoteId(n.id);
  }
  async function removeNote(n) {
    if (noteTimers.current[n.id]) { clearTimeout(noteTimers.current[n.id]); delete noteTimers.current[n.id]; }
    try { if (n.saved) await deleteNote(clubId, n.id); }
    catch (e) { console.error("Note delete error:", e); alert("Verwijderen is niet gelukt."); return; }
    setNotesList((notesRef.current || []).filter(function (x) { return x.id !== n.id; }));
    setSelNoteId(null);
  }
  function openNoteLesson(n) {
    var l = levels && levels.find(function (x) { return x.id === n.planId; });
    if (!l || !data || !data[l.id] || !data[l.id][n.week]) { alert("Deze les staat niet meer in de actieve seizoensplannen."); return; }
    setSel(l.id); setSelW(n.week); setTab(n.les ? 1 : 0); setScr("lesson");
  }
  // Notitiepaneel onder een les (seizoensoverzicht en weekoverzicht).
  function renderNotes(level, week, les, compact) {
    if (!profile || !level) return null;
    if (notes === null) return <p style={{ fontSize: 13, color: T.muted, margin: "4px 2px" }}>Notities laden…</p>;
    var id = lessonNoteId(profile.uid, level.id, week, les);
    var mine = notes.find(function (x) { return x.id === id; }) || null;
    var others = notes.filter(function (x) { return x.kind === "lesson" && x.shared && x.ownerUid !== profile.uid && x.planId === level.id && Number(x.week) === Number(week) && (x.les ? 1 : 0) === (les ? 1 : 0); });
    return <NotesPanel mine={mine} others={others} solo={solo} compact={compact} status={mine ? noteStatus : ""}
      label={compact ? null : lessonLabelOf(level, week, les)}
      onText={function (t) { editLessonNote(level, week, les, { text: t }); }}
      onShare={function (v) { editLessonNote(level, week, les, { shared: v }); }} />;
  }

  function confirmDel(type, id) { setDelT({ type: type, id: id }); setMdl("confirmDel"); }
  async function openLevel(l) {
    if (l.archived && l.type !== "drills") await ensureArchive(l.id);
    setSel(l.id); setCurFolder(null); setDrillFilter([]); setScr(l.type === "drills" ? "drills" : "weeks");
  }
  function openEditLevel(id) {
    var l = levels.find(function (x) { return x.id === id; });
    var ci = COLORS.findIndex(function (c) { return c.color === (l.color || "").toUpperCase(); });
    setEditLvl(id); setEditName(l.label); setEditColor(ci); setMdl("editLevel"); // -1 = eigen kleur van vroeger, blijft dan staan
  }
  // Volgorde van seizoensplannen (of oefeningen-secties) aanpassen, binnen de eigen
  // sectie: concept, seizoensplannen, archief of oefeningen. De volgorde in de levels-
  // lijst bepaalt ook de volgorde in het weekoverzicht.
  function sectionOf(l) { if (l.type === "drills") return "d"; if (l.archived) return "a"; if (l.concept) return "c"; return "p"; }
  function sectionItems(id) {
    var l0 = levels.find(function (x) { return x.id === id; });
    if (!l0) return [];
    var sec = sectionOf(l0);
    return levels.filter(function (l) { return sectionOf(l) === sec; });
  }
  function moveLevel(id, to) {
    var l0 = levels.find(function (x) { return x.id === id; });
    if (!l0) return;
    var sec = sectionOf(l0);
    var slots = []; levels.forEach(function (l, i) { if (sectionOf(l) === sec) slots.push(i); });
    var items = slots.map(function (i) { return levels[i]; });
    var from = items.findIndex(function (x) { return x.id === id; });
    setMdl(null); setMoveLvlId(null);
    if (from < 0 || to < 0 || to >= items.length || from === to) return;
    var it = items.splice(from, 1)[0];
    items.splice(to, 0, it);
    var nl = levels.slice();
    slots.forEach(function (s, k) { nl[s] = items[k]; });
    persistLevelsAndDrills(nl, null);
  }
  function openMoveLevel(id) { setMoveLvlId(id); setMdl("moveLevel"); }
  function openDuplicatePlan(id) {
    ensureArchive(id);
    var l = levels.find(function (x) { return x.id === id; });
    setDupSourceId(id); setDupName(l ? l.label + " (kopie)" : ""); setDupColor(0); setMdl("duplicatePlan");
  }
  function openCopyWeekTo(w) { setCopyWeekSrc(w); setMdl("copyWeekTo"); }
  function openTimeEdit(lesKey, exKey) { setTimeEdit({ lesKey: lesKey, exKey: exKey }); setCustomTime(""); setMdl("editTime"); }
  function applyTime(v) {
    if (!timeEdit) return;
    var n = typeof v === "number" ? v : parseDur(v);
    if (n == null || isNaN(n) || n < 0) return;
    setEx(timeEdit.lesKey, timeEdit.exKey, "time", n);
    setMdl(null); setTimeEdit(null); setCustomTime("");
  }

  var lv = sel === EXTRA_ID ? EXTRA_LEVEL : (levels ? levels.find(function (l) { return l.id === sel; }) : null);
  var sectionCanEdit = sel === EXTRA_ID ? canEditExtra : canEdit;
  var wks = (data && sel && data[sel]) ? Object.keys(data[sel]).map(Number).sort(function (a, b) { return a - b; }) : [];
  var pd = mob ? 12 : 20;
  var plans = levels ? levels.filter(function (l) { return l.type !== "drills" && !l.archived && !l.concept; }) : [];
  // Concept en archief zijn alleen zichtbaar vanaf editor, en vallen weg in Bekijkmodus
  // (dan zie je de app zoals een trainer hem gebruikt).
  var showManage = !!(levels && canEditRole && !viewOnly);
  // null = sectie niet tonen (trainer of Bekijkmodus); [] = lege sectie tonen.
  var conceptPlans = showManage ? levels.filter(function (l) { return l.type !== "drills" && l.concept && !l.archived; }) : null;
  var archivedPlans = showManage ? levels.filter(function (l) { return l.type !== "drills" && l.archived; }) : [];
  var dsecs = (levels && hasDeck) ? levels.filter(function (l) { return l.type === "drills"; }) : [];
  var extraCount = drills && drills[EXTRA_ID] ? drills[EXTRA_ID].filter(function (x) { return x.type === "drill"; }).length : 0;
  var showExtra = canEditExtra || extraCount > 0;
  var legacyCount = extraCount;
  // Training Deck in de bibliotheek: de gekozen sectie (anders de eerste).
  var deckSecSel = dsecs.some(function (d) { return d.id === sel; }) ? sel : (dsecs[0] ? dsecs[0].id : null);
  var otherPlans = (levels && sel) ? levels.filter(function (l) { return l.type !== "drills" && l.id !== sel; }) : [];

  if (loading) return (<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: BF, fontSize: 19, color: "#1C1B19", fontWeight: 800 }}>LADEN...</div>);

  var showPublishBanner = canEdit && hasUnpublished;

  function renderModal() {
    if (mdl === "addChoice") return (<Modal onClose={function () { setMdl(null); }}>
      <h3 style={{ fontFamily: BF, fontSize: 18, marginBottom: 16, color: "#1C1B19", fontWeight: 800 }}>Wat wil je toevoegen?</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={function () { setMdl("addPlan"); }} style={{ padding: "16px 20px", borderRadius: 12, background: "#1A2744", color: "#fff", border: "none", cursor: "pointer", textAlign: "left" }}><div style={{ fontFamily: BF, fontSize: 14, marginBottom: 2, fontWeight: 800 }}>📋 Seizoensplan</div><div style={{ fontSize: 12, opacity: .7 }}>Lesplan met weken en oefeningen</div></button>
        <button onClick={function () { setMdl("addDrills"); }} style={{ padding: "16px 20px", borderRadius: 12, background: "#fff", color: "#1C1B19", border: "2px solid #1A2744", cursor: "pointer", textAlign: "left" }}><div style={{ fontFamily: BF, fontSize: 14, marginBottom: 2, fontWeight: 800 }}>🎾 Oefeningen-sectie</div><div style={{ fontSize: 12, color: "#888" }}>Losse oefeningen met uitleg en foto's</div></button>
      </div>
    </Modal>);
    if (mdl === "addPlan" || mdl === "addDrills") {
      var isDr = mdl === "addDrills";
      return (<Modal onClose={function () { setMdl(null); }}>
        <h3 style={{ fontFamily: BF, fontSize: 18, marginBottom: 14, color: "#1C1B19", fontWeight: 800 }}>{isDr ? "Oefeningen-sectie" : "Seizoensplan"}</h3>
        <input value={nn} onChange={function (e) { setNn(e.target.value); }} placeholder="Naam" autoFocus style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, outline: "none", marginBottom: 12, fontFamily: FF }} />
        <p style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Kleur:</p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>{COLORS.map(function (c, i) { return <button key={i} onClick={function () { setNc(i); }} style={{ width: 28, height: 28, borderRadius: "50%", background: c.color, border: nc === i ? "3px solid #1A2744" : "3px solid transparent", cursor: "pointer" }} />; })}</div>
        <button onClick={function () { addLevel(isDr ? "drills" : "plan"); }} disabled={!nn.trim()} style={{ width: "100%", padding: 10, borderRadius: 8, background: nn.trim() ? "#1A2744" : "#ccc", color: "#fff", border: "none", cursor: nn.trim() ? "pointer" : "default", fontSize: 14, fontWeight: 700 }}>Toevoegen</button>
      </Modal>);
    }
    if (mdl === "editLevel") return (<Modal onClose={function () { setMdl(null); }}>
      <h3 style={{ fontFamily: BF, fontSize: 18, marginBottom: 14, color: "#1C1B19", fontWeight: 800 }}>Naam en kleur aanpassen</h3>
      <input value={editName} onChange={function (e) { setEditName(e.target.value); }} onKeyDown={function (e) { if (e.key === "Enter") renameLevel(); }} placeholder="Nieuwe naam" autoFocus style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, outline: "none", marginBottom: 12, fontFamily: FF }} />
      <p style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Kleur:</p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>{COLORS.map(function (c, i) { return <button key={i} onClick={function () { setEditColor(i); }} title={"Kleur " + (i + 1)} style={{ width: 28, height: 28, borderRadius: "50%", background: c.color, border: editColor === i ? "3px solid #1A2744" : "3px solid transparent", boxShadow: editColor === i ? "0 0 0 2px #fff inset" : "none", cursor: "pointer" }} />; })}</div>
      <button onClick={renameLevel} disabled={!editName.trim()} style={{ width: "100%", padding: 10, borderRadius: 8, background: editName.trim() ? "#1A2744" : "#ccc", color: "#fff", border: "none", cursor: editName.trim() ? "pointer" : "default", fontSize: 14, fontWeight: 700 }}>Opslaan</button>
    </Modal>);
    if (mdl === "duplicatePlan") return (<Modal onClose={function () { setMdl(null); setDupSourceId(null); }}>
      <h3 style={{ fontFamily: BF, fontSize: 18, marginBottom: 14, color: "#1C1B19", fontWeight: 800 }}>Seizoensplan dupliceren</h3>
      <input value={dupName} onChange={function (e) { setDupName(e.target.value); }} placeholder="Naam van de kopie" autoFocus style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, outline: "none", marginBottom: 12, fontFamily: FF }} />
      <p style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Kleur:</p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>{COLORS.map(function (c, i) { return <button key={i} onClick={function () { setDupColor(i); }} style={{ width: 28, height: 28, borderRadius: "50%", background: c.color, border: dupColor === i ? "3px solid #1A2744" : "3px solid transparent", cursor: "pointer" }} />; })}</div>
      <button onClick={duplicatePlan} disabled={!dupName.trim()} style={{ width: "100%", padding: 10, borderRadius: 8, background: dupName.trim() ? "#1A2744" : "#ccc", color: "#fff", border: "none", cursor: dupName.trim() ? "pointer" : "default", fontSize: 14, fontWeight: 700 }}>Dupliceren</button>
    </Modal>);
    if (mdl === "confirmDel") return (<Modal onClose={function () { setMdl(null); }}>
      <h3 style={{ fontFamily: BF, fontSize: 18, color: "#E53E3E", marginBottom: 12, fontWeight: 800 }}>Verwijderen?</h3>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={function () { setMdl(null); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#f0f0f0", border: "none", cursor: "pointer", fontWeight: 700, color: "#555" }}>Annuleren</button>
        <button onClick={function () { if (delT.type === "level") removeLevel(delT.id); else if (delT.type === "week") removeWeek(delT.id); else if (delT.type === "drill") removeDrillItem(delT.id); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#E53E3E", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700 }}>Verwijderen</button>
      </div>
    </Modal>);
    if (mdl === "moveLevel") {
      var secItems = sectionItems(moveLvlId);
      return (<Modal onClose={function () { setMdl(null); setMoveLvlId(null); }}>
        <h3 style={{ fontFamily: BF, fontSize: 16, marginBottom: 6, color: "#1C1B19", fontWeight: 800 }}>Verplaatsen naar</h3>
        <p style={{ fontSize: 12, color: "#888", marginBottom: 14, fontFamily: FF }}>Kies de plek waar dit plan moet komen te staan.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {secItems.map(function (l, i) {
            var cur = l.id === moveLvlId;
            return (<button key={l.id} disabled={cur} onClick={function () { moveLevel(moveLvlId, i); }} style={{ padding: "10px 12px", borderRadius: 8, background: cur ? "#E9EDF5" : "#f5f5f0", border: "1px solid " + (cur ? "#1A2744" : "#E2DED5"), cursor: cur ? "default" : "pointer", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#1C1B19", fontFamily: FF, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 20, color: "#999", fontWeight: 800 }}>{i + 1}</span>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: l.color, flex: "none" }} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.label}</span>
              {cur && <span style={{ fontSize: 11, color: "#6B675F", fontWeight: 600 }}>huidige plek</span>}
            </button>);
          })}
        </div>
      </Modal>);
    }
    if (mdl === "moveWeek") return (<Modal onClose={function () { setMdl(null); setMoveWeekSrc(null); }}>
      <h3 style={{ fontFamily: BF, fontSize: 16, marginBottom: 6, color: "#1C1B19", fontWeight: 800 }}>{"Week " + moveWeekSrc + " verplaatsen naar"}</h3>
      <p style={{ fontSize: 12, color: "#888", marginBottom: 14, fontFamily: FF }}>Kies de nieuwe plek. De andere weken schuiven op en worden opnieuw genummerd.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))", gap: 6 }}>
        {wks.map(function (w) {
          var cur = w === moveWeekSrc;
          return (<button key={w} disabled={cur} onClick={function () { moveWeek(moveWeekSrc, w); }} style={{ height: 40, borderRadius: 8, border: "1px solid " + (cur ? (lv ? lv.color : "#1A2744") : "#E2DED5"), background: cur ? (lv ? lv.color : "#1A2744") : "#f5f5f0", color: cur ? (lv && lv.on ? lv.on : "#fff") : "#1C1B19", fontWeight: 800, fontSize: 14, cursor: cur ? "default" : "pointer", fontFamily: FF }}>{w}</button>);
        })}
      </div>
    </Modal>);
    if (mdl === "copyWeekTo") return (<Modal onClose={function () { setMdl(null); setCopyWeekSrc(null); }}>
      <h3 style={{ fontFamily: BF, fontSize: 16, marginBottom: 6, color: "#1C1B19", fontWeight: 800 }}>Week kopiëren naar</h3>
      <p style={{ fontSize: 12, color: "#888", marginBottom: 14, fontFamily: FF }}>Komt terecht aan het eind van het gekozen seizoensplan.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 300, overflowY: "auto" }}>
        {otherPlans.length === 0 && <p style={{ fontSize: 12, color: "#999" }}>Geen ander seizoensplan om naartoe te kopiëren.</p>}
        {otherPlans.map(function (l) {
          return (<button key={l.id} onClick={function () { copyWeekToPlan(copyWeekSrc, l.id); }} style={{ padding: "10px 14px", borderRadius: 8, background: "#f5f5f0", border: "1px solid #E2DED5", cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#1C1B19", fontFamily: FF, display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: l.color, display: "inline-block" }} />{l.label}{l.archived ? " (archief)" : ""}</button>);
        })}
      </div>
    </Modal>);
    if (mdl === "editTime") {
      var curVal = (timeEdit && data && data[sel] && data[sel][selW]) ? ((data[sel][selW][timeEdit.lesKey] || {})[timeEdit.exKey] || {}).time || 0 : 0;
      return (<Modal onClose={function () { setMdl(null); setTimeEdit(null); }}>
        <h3 style={{ fontFamily: BF, fontSize: 16, marginBottom: 14, color: "#1C1B19", fontWeight: 800 }}>Aantal minuten</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {MINUTE_PRESETS.map(function (m) {
            var active = curVal === m;
            return (<button key={m} onClick={function () { applyTime(m); }} style={{ padding: "14px 18px", borderRadius: 10, border: active ? "2px solid #1A2744" : "1px solid #ccc", background: active ? "#1A2744" : "#fff", color: active ? "#fff" : "#1A2744", cursor: "pointer", fontFamily: BF, fontSize: 14, minWidth: 64, fontWeight: 800 }}>{m} min</button>);
          })}
        </div>
        <p style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Afwijkend aantal minuten (halve minuten mag: 7:30 of 7,5):</p>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="text" inputMode="decimal" aria-label="Afwijkend aantal minuten" value={customTime} onChange={function (e) { setCustomTime(e.target.value); }} placeholder={"bv. 7:30 (nu " + fmtDur(curVal) + ")"} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, outline: "none", fontFamily: FF }} />
          <button onClick={function () { applyTime(customTime === "" ? curVal : customTime); }} disabled={customTime === ""} style={{ padding: "10px 16px", borderRadius: 8, background: customTime !== "" ? "#1A2744" : "#ccc", color: "#fff", border: "none", cursor: customTime !== "" ? "pointer" : "default", fontSize: 13, fontWeight: 700 }}>Toepassen</button>
        </div>
      </Modal>);
    }
    if (mdl === "copyPlanLib") {
      var cpPlan = levels.find(function (l) { return l.id === copyPlanId; });
      var cpCount = cpPlan ? planLessons([cpPlan], data).length : 0;
      var cpExisting = libItems ? libItems.filter(function (x) { return x.src === "plan" && x.fromPlan && x.fromPlan.id === copyPlanId; }).length : null;
      return (<Modal onClose={function () { if (!copyingPlan) { setMdl(null); setCopyPlanId(null); } }}>
        <h3 style={{ fontFamily: BF, fontSize: 18, marginBottom: 8, color: "#1C1B19", fontWeight: 800 }}>Kopiëren naar losse lessen</h3>
        <p style={{ fontSize: 14, color: "#4A4740", marginBottom: 8, lineHeight: 1.5, fontFamily: FF }}>
          {cpCount === 0
            ? "Dit seizoensplan heeft nog geen ingevulde lessen om te kopiëren."
            : cpCount + (cpCount === 1 ? " ingevulde les" : " ingevulde lessen") + " uit ‘" + (cpPlan ? cpPlan.label : "") + "’ komen in de bibliotheek onder Lessen › Seizoensplan, voor alle clubs zichtbaar."}
        </p>
        {cpExisting > 0 && <p style={{ fontSize: 13, color: "#8A430D", background: "#FDF1E7", border: "1px solid #F3CDAE", borderRadius: 8, padding: "8px 10px", marginBottom: 8, lineHeight: 1.5, fontFamily: FF }}>
          {cpExisting + (cpExisting === 1 ? " les" : " lessen") + " uit dit plan staan er al. Die worden overschreven met de versie van nu (ook als je ze in de bibliotheek hebt aangepast)."}
        </p>}
        <p style={{ fontSize: 12, color: "#6B675F", marginBottom: 14, lineHeight: 1.5, fontFamily: FF }}>Het seizoensplan zelf verandert niet. Lege lessen worden overgeslagen.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={function () { setMdl(null); setCopyPlanId(null); }} disabled={copyingPlan} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#f0f0f0", border: "none", cursor: "pointer", fontWeight: 700, color: "#555", minHeight: 44 }}>Annuleren</button>
          <button onClick={copyPlanToLibrary} disabled={copyingPlan || cpCount === 0} style={{ flex: 1, padding: 10, borderRadius: 8, background: cpCount ? "#1A2744" : "#ccc", color: "#fff", border: "none", cursor: cpCount ? "pointer" : "default", fontWeight: 700, minHeight: 44 }}>{copyingPlan ? "Bezig..." : "Kopiëren"}</button>
        </div>
      </Modal>);
    }
    if (mdl === "moveTo") {
      var allFolders = drills[sel].filter(function (x) { return x.type === "folder"; });
      var itemToMove = drills[sel].find(function (x) { return x.id === moveItem; });
      var folderPath = function (folderId) {
        var path = [];
        var cur = allFolders.find(function (x) { return x.id === folderId; });
        while (cur) { path.unshift(cur.name); cur = cur.parentId ? allFolders.find(function (x) { return x.id === cur.parentId; }) : null; }
        return path.join(" / ");
      };
      return (<Modal onClose={function () { setMdl(null); setMoveItem(null); }}>
        <h3 style={{ fontFamily: BF, fontSize: 16, marginBottom: 6, color: "#1C1B19", fontWeight: 800 }}>Verplaatsen</h3>
        <p style={{ fontSize: 12, color: "#888", marginBottom: 14, fontFamily: FF }}>{itemToMove ? (itemToMove.type === "folder" ? "📁 " : "") + (itemToMove.name || "Naamloos") : ""}</p>
        <p style={{ fontSize: 11, color: "#999", marginBottom: 8, fontFamily: FF }}>Kies een bestemming:</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 300, overflowY: "auto" }}>
          <button onClick={function () { moveDrillTo(null); }} style={{ padding: "10px 14px", borderRadius: 8, background: "#f5f5f0", border: "1px solid #E2DED5", cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#1C1B19", fontFamily: FF }}>🏠 Hoofdmap</button>
          {allFolders.map(function (f) {
            if (itemToMove && itemToMove.type === "folder") {
              var isDesc = function (fid) {
                if (!fid) return false;
                if (fid === itemToMove.id) return true;
                var pf = allFolders.find(function (x) { return x.id === fid; });
                return pf ? isDesc(pf.parentId) : false;
              };
              if (isDesc(f.id)) return null;
            }
            return (<button key={f.id} onClick={function () { moveDrillTo(f.id); }} style={{ padding: "10px 14px", borderRadius: 8, background: "#f5f5f0", border: "1px solid #E2DED5", cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#1C1B19", fontFamily: FF }}>📁 {folderPath(f.id)}</button>);
          })}
        </div>
      </Modal>);
    }
    return null;
  }

  function renderScreen() {
  if (scr === "home") return (
    <Home mob={mob} pd={pd} plans={plans} conceptPlans={conceptPlans} archivedPlans={archivedPlans} dsecs={dsecs} canEdit={canEdit}
      canEditRole={canEditRole} viewOnly={viewOnly} setViewOnly={setViewOnly}
      showExtra={showExtra} extraCount={extraCount} hasDeck={hasDeck} onOpenAccounts={function () { setScr("accounts"); }}
      openLevel={openLevel} openEditLevel={openEditLevel} confirmDel={confirmDel}
      openDuplicatePlan={openDuplicatePlan} onCopyToLibrary={function (id) { ensureArchive(id); setCopyPlanId(id); setMdl("copyPlanLib"); }}
      onOpenWeek={function () { switchMode("week"); }} onOpenLibrary={function () { setScr("bib"); }} toggleArchive={toggleArchive} toggleConcept={toggleConcept} moveLevel={moveLevel} openMoveLevel={openMoveLevel}
      setMdl={setMdl} setNn={setNn} setNc={setNc} renderModal={renderModal} savLabel={savLabel} sav={sav}
      showPublishBanner={showPublishBanner} publishing={publishing} onPublish={publish} justPublished={justPublished} />
  );

  if (scr === "planning" && canEditRole) return (
    <Planning mob={mob} pd={pd} clubName={club ? (club.name || club.slug) : ""} planning={planning}
      lessonWeeks={maxWeekOf(levels, data)} onChange={persistPlanning} onBack={function () { setScr(lastOv); }} sav={sav} />
  );

  if (scr === "accounts" && atLeast(profile, "hoofdtrainer")) return (
    <Accounts mob={mob} pd={pd} onBack={function () { setScr("home"); }} />
  );

  // Favoriete oefeningen uit de Training Deck, als items voor de bibliotheek.
  var deckFavItems = [];
  if (favOnly && drills) dsecs.forEach(function (sec) {
    (drills[sec.id] || []).forEach(function (d) {
      var k = deckFavKey(sec.id, d.id);
      if (d.type === "drill" && isFav(k)) deckFavItems.push({ id: k, favKey: k, src: "deck", kind: "drill", name: d.name, tags: d.tags || [], photo: d.photo, diagram: d.diagram, secId: sec.id, drillId: d.id, secLabel: sec.label, lv: sec });
    });
  });

  if (scr === "bib") return (
    <Library mob={mob} isFav={favs ? isFav : null} toggleFav={profile ? toggleFav : null} favOnly={favOnly} setFavOnly={setFavOnly}
      deckFavItems={deckFavItems} onOpenDeck={function (it) { setSel(it.secId); setSelDi(it.drillId); setScr("drillD"); }} pd={pd} sav={sav} kind={libKind} setKind={function (k) { setLibKind(k); setLibTags([]); }}
      lesSrc={lesSrc} setLesSrc={function (v) { setLesSrc(v); setLibTags([]); }} oefSrc={oefSrc} setOefSrc={function (v) { setOefSrc(v); setLibTags([]); }}
      color={libColor} setColor={function (c) { setLibColor(c); setLibTags([]); }} tags={libTags} setTags={setLibTags}
      canEditOfficial={canEdit} planColors={planColorMap(levels)} libItems={libItems || []} libLoading={libItems === null} libError={libError}
      uid={profile ? profile.uid : null} solo={solo} hasDeck={hasDeck}
      dsecs={dsecs} drills={drills} canEditDeck={canEdit} deckSel={deckSecSel}
      pickSection={function (l) { setSel(l.id); setCurFolder(null); setDrillFilter([]); }}
      deckContent={deckSecSel && lv && lv.type === "drills" ? (
        <Drills embedded mob={mob} pd={pd} lv={lv} sel={sel} drills={drills} sav={sav}
          curFolder={curFolder} setCurFolder={setCurFolder} drillFilter={drillFilter} setDrillFilter={setDrillFilter}
          canEdit={canEdit} setSelDi={setSelDi} setScr={setScr} setMoveItem={setMoveItem} setMdl={setMdl} confirmDel={confirmDel}
          addDrillItem={addDrillItem} addFolder={addFolder} renameFolder={renameFolder} renderModal={renderModal}
          isFav={function (id) { return isFav(deckFavKey(deckSecSel, id)); }} toggleFav={profile ? function (id) { toggleFav(deckFavKey(deckSecSel, id)); } : null} />
      ) : null}
      openEditLevel={openEditLevel} confirmDel={confirmDel} moveLevel={moveLevel}
      onAddSection={function () { setNn(""); setNc(6); setMdl("addDrills"); }}
      legacyCount={legacyCount} canMigrate={canEditExtra && legacyCount > 0} migrating={migrating} onMigrate={migrateLegacy}
      openLegacy={function () { openLevel(EXTRA_LEVEL); }}
      notesContent={<NotesBoard mob={mob} notes={notes || []} uid={profile ? profile.uid : null} loading={notes === null} error={notesError}
        selId={selNoteId} setSelId={setSelNoteId} onNew={newFreeNote} onChange={changeNote} onDelete={removeNote} onOpenLesson={openNoteLesson} status={noteStatus}
        isFav={isFav} toggleFav={profile ? toggleFav : null} favOnly={favOnly} setFavOnly={setFavOnly} />}
      onNew={newLib} onOpen={function (it) { setLibSel({ type: "lib", id: it.id }); setScr("libItem"); }}
      renderModal={renderModal} />
  );

  if (scr === "libItem") {
    var libItem = !libSel ? null : (libItems || []).find(function (x) { return x.id === libSel.id; });
    if (!libItem) return (
      <div style={{ padding: 40, textAlign: "center", color: T.muted, fontFamily: FF }}>
        {libItems === null && libSel ? "Laden..." : <>Deze les of oefening bestaat niet meer. <button type="button" onClick={function () { setScr("bib"); }} style={{ border: 0, background: "none", color: T.primary, fontWeight: 700, cursor: "pointer", fontFamily: FF }}>Terug naar de bibliotheek</button></>}
      </div>
    );
    var own = libItem.src !== "plan" && owner && libItem.ownerUid === owner.uid;
    return (
      <LibItem mob={mob} pd={pd} item={libItem} sav={sav} clubId={clubId} clubName={clubLabel} solo={solo}
        isOwn={own && !viewOnly} canModerate={atLeast(profile, "hoofdtrainer") && !viewOnly} canEditOfficial={canEdit} planColors={planColorMap(levels)}
        backLabel={libItem.kind === "lesson" ? "Lessen" : "Oefeningen"}
        onBack={function () { backFromLib(libItem); }}
        onField={function (f, v) { changeLib(libItem.id, function (c) { c[f] = v; }); }}
        onPart={function (pk, f, v) { changeLib(libItem.id, function (c) { if (!c.parts) c.parts = {}; if (!c.parts[pk]) c.parts[pk] = { description: "", accents: [], time: 0 }; c.parts[pk][f] = v; }); }}
        onCopy={function () { copyLib(libItem); }}
        onDelete={function () { removeLib(libItem.id); }}
        fav={isFav(libFavKey(libItem))} onToggleFav={profile ? function () { toggleFav(libFavKey(libItem)); } : null} />
    );
  }

  if (scr === "drills") return (
    <Drills mob={mob} pd={pd} lv={lv} sel={sel} drills={drills} sav={sav}
      onBack={function () { setScr("bib"); }} backLabel="Bibliotheek"
      curFolder={curFolder} setCurFolder={setCurFolder} drillFilter={drillFilter} setDrillFilter={setDrillFilter}
      canEdit={sectionCanEdit} setSelDi={setSelDi} setScr={setScr} setMoveItem={setMoveItem} setMdl={setMdl} confirmDel={confirmDel}
      addDrillItem={addDrillItem} addFolder={addFolder} renameFolder={renameFolder}
      renderModal={renderModal} />
  );

  if (scr === "drillD" && selDi !== null) return (
    <DrillDetail mob={mob} pd={pd} lv={lv} sel={sel} selDi={selDi} drills={drills} sav={sav} clubId={clubId} canEdit={sectionCanEdit}
      setSelDi={setSelDi} setScr={function (s) { setScr(s === "drills" && sel !== EXTRA_ID ? "bib" : s); }} setDrillField={setDrillField} setMoveItem={setMoveItem} setMdl={setMdl} confirmDel={confirmDel}
      renderModal={renderModal}
      fav={isFav(deckFavKey(sel, selDi))} onToggleFav={profile && sel !== EXTRA_ID ? function () { toggleFav(deckFavKey(sel, selDi)); } : null} />
  );

  if (scr === "weeks") return (
    <Weeks mob={mob} pd={pd} lv={lv} sel={sel} data={data} wks={wks}
      weekFilter={weekFilter} setWeekFilter={setWeekFilter} canEdit={canEdit}
      setSelW={setSelW} setTab={setTab} setScr={setScr} confirmDel={confirmDel} addWeek={addWeek}
      duplicateWeek={duplicateWeek} openCopyWeekTo={openCopyWeekTo} moveWeek={moveWeek} openMoveWeek={openMoveWeek}
      renderModal={renderModal} sav={sav} savLabel={savLabel}
      showPublishBanner={showPublishBanner} publishing={publishing} onPublish={publish} justPublished={justPublished} />
  );

  if (scr === "lesson") return (
    <Lesson mob={mob} pd={pd} lv={lv} sel={sel} selW={selW} data={data} wks={wks} tab={tab} sav={sav} savLabel={savLabel} canEdit={canEdit} planning={planning}
      setSelW={setSelW} setTab={setTab} setScr={setScr}
      setField={setField} setEx={setEx} setLesson={setLesson} setExtras={setExtras} setWeekTags={setWeekTags} openTimeEdit={openTimeEdit}
      renderNotes={function (level, week, les) { return renderNotes(level, week, les, mob); }}
      renderModal={renderModal}
      showPublishBanner={showPublishBanner} publishing={publishing} onPublish={publish} justPublished={justPublished} />
  );

  return null;
  }
  var weekPlans = plans;
  function switchMode(m) {
    if (m === "week") {
      // Neem de context mee: vanuit een plan/week meteen naar die groep en week.
      var pi = weekPlans.findIndex(function (p) { return p.id === sel; });
      if ((scr === "weeks" || scr === "lesson") && pi >= 0) setWvSel(pi);
      if (scr === "lesson" && selW) { setWvWeek(selW); setWvTab(function (m) { var n = Object.assign({}, m); n[sel] = tab; return n; }); }
      setScr("week");
    } else {
      setScr(lastOv === "lesson" && !selW ? "home" : lastOv);
    }
  }
  function editFromWeek(planId, w) { setSel(planId); setSelW(w); setTab(wvTab[planId] || 0); setScr("lesson"); }

  var isWeek = scr === "week";
  var isOverview = scr === "home" || scr === "weeks" || scr === "lesson";
  var isLib = scr === "bib" || scr === "libItem" || scr === "drills" || scr === "drillD";
  var clubLabel = club ? (club.name || club.slug) : "";
  function goHome() { setScr("home"); }

  // Navigatie (build 2.5): op laptop de zijbalk met Seizoensoverzicht, Weekoverzicht en
  // Bibliotheek (+ beheer); op telefoon dezelfde drie in een balk onderin, en het beheer
  // en account in het uitklapmenu bovenin.
  var mainItems = [
    { key: "seizoen", label: "Seizoensoverzicht", icon: "home", active: isOverview, onClick: function () { if (isOverview) goHome(); else switchMode("overzicht"); } },
    { key: "week", label: "Weekoverzicht", icon: "week", active: isWeek, onClick: function () { switchMode("week"); } },
    { key: "bib", label: "Bibliotheek", icon: "library", active: isLib, onClick: function () { setScr("bib"); } },
  ];
  var adminItems = [];
  if (canEditRole) adminItems.push({ key: "planning", label: "Seizoensplanning", icon: "planning", active: scr === "planning", onClick: function () { setScr("planning"); } });
  if (atLeast(profile, "hoofdtrainer")) adminItems.push({ key: "accounts", label: "Accounts", icon: "accounts", active: scr === "accounts", onClick: function () { setScr("accounts"); } });
  if (atLeast(profile, "creator")) adminItems.push({ key: "beheer", label: "Beheer", icon: "beheer", onClick: function () { navigate("/beheer"); } });
  var navItems = adminItems.length ? mainItems.concat([{ heading: "BEHEER" }], adminItems) : mainItems;
  var account = profile ? {
    name: profile.name || profile.email,
    role: roleLabel(profile.role),
    sub: roleLabel(profile.role) + (clubLabel ? " · " + clubLabel : ""),
    viewOnly: viewOnly,
    // Bekijkmodus is alleen nuttig voor wie iets kan bewerken (vanaf hoofdtrainer).
    setViewOnly: atLeast(profile, "hoofdtrainer") ? setViewOnly : null,
    actions: [
      { key: "pw", label: "Wachtwoord wijzigen", icon: "key", onClick: function () { setAcctModal("pw"); } },
      { key: "roles", label: "Uitleg rollen", icon: "info", onClick: function () { setAcctModal("roles"); } },
      { key: "out", label: "Uitloggen", icon: "logout", danger: true, onClick: function () { logout(); } },
    ],
  } : null;

  var hasPublish = showPublishBanner || justPublished;
  var publishBtn = <PublishBanner show={showPublishBanner} publishing={publishing} onPublish={publish} justPublished={justPublished} />;
  // Laptop: de publiceerknop in een smalle balk bovenaan (alleen als er iets te publiceren valt).
  var modeBar = (wide && hasPublish) ? (
    <div style={{ position: "sticky", top: 0, zIndex: 50, background: T.bg, borderBottom: "1px solid " + T.line, flex: "none" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "10px 24px", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>{publishBtn}</div>
    </div>
  ) : null;

  return (
    <div style={Object.assign({ display: "flex", alignItems: "flex-start", minHeight: "100vh", background: T.bg, fontFamily: FF },
      isWeek ? { height: "100dvh", overflow: "hidden" } : {})}>
      {wide && <SideNav subtitle={clubLabel} onBrand={goHome} items={navItems} account={account} collapsed={collapsed} onToggle={toggleCollapsed} />}
      <div style={isWeek
        ? { flex: 1, minWidth: 0, height: "100dvh", display: "flex", flexDirection: "column", overflow: "hidden" }
        : { flex: 1, minWidth: 0 }}>
        {!wide && <MobileBar subtitle={clubLabel} onBrand={goHome} items={adminItems.length ? [{ heading: "BEHEER" }].concat(adminItems) : []} account={account} right={hasPublish ? publishBtn : null} />}
        {modeBar}
        {isWeek
          ? <WeekView wide={wide} mob={mob} plans={weekPlans} data={data} planning={planning} week={wvWeek} setWeek={setWvWeek}
              sel={wvSel} setSel={setWvSel} lesTabs={wvTab} setLesTab={function (id, v) { setWvTab(function (m) { var n = Object.assign({}, m); n[id] = v; return n; }); }} canEdit={canEdit} onEdit={editFromWeek}
              renderNotes={function (level, week, les) { return renderNotes(level, week, les, true); }} />
          : renderScreen()}
        {!wide && !isWeek && <div aria-hidden="true" style={{ height: BOTTOM_BAR_SPACE }} />}
        {!wide && <BottomBar items={mainItems} fixed={!isWeek} />}
      </div>
      {acctModal === "pw" && <ChangePasswordModal onClose={function () { setAcctModal(null); }} />}
      {acctModal === "roles" && <RolesModal onClose={function () { setAcctModal(null); }} />}
    </div>
  );
}

// Hoogste aantal weken over de actieve seizoensplannen (bepaalt hoe ver de planning
// doorloopt).
function maxWeekOf(levels, data) {
  var max = 1;
  (levels || []).forEach(function (l) {
    if (l.type === "drills" || l.archived || l.concept) return;
    var n = data && data[l.id] ? Object.keys(data[l.id]).length : 0;
    if (n > max) max = n;
  });
  return max;
}
